/**
 * App environment config — single source of truth for env var access.
 * Vite replaces import.meta.env.* at build/dev time.
 * In tests, mock this module instead of polyfilling import.meta.
 */

export const IS_DEV = !!import.meta.env.DEV;

export const env = {
  REACT_APP_ENTRA_TENANT_ID: import.meta.env.REACT_APP_ENTRA_TENANT_ID || "",
  REACT_APP_ENTRA_CLIENT_ID: import.meta.env.REACT_APP_ENTRA_CLIENT_ID || "",
  REACT_APP_ENTRA_REDIRECT_URI: import.meta.env.REACT_APP_ENTRA_REDIRECT_URI || "",
  REACT_APP_CATALOG_URL: import.meta.env.REACT_APP_CATALOG_URL || "",
  // Adoption artifact (Contract 7), joined onto the catalog at read time. Empty is
  // the normal case: fetchAdoption then derives adoption.json as a sibling of the
  // catalog URL, which is where the refresh job publishes it. Set this to point
  // local dev at the committed fixture: REACT_APP_ADOPTION_URL=/mock-adoption.json
  REACT_APP_ADOPTION_URL: import.meta.env.REACT_APP_ADOPTION_URL || "",
  // Community vote tallies (Contract 8). Empty is the normal case: the client then
  // reads them from the feedback API's /feedback/tallies endpoint. Set this to point
  // local dev at the committed fixture instead — REACT_APP_FEEDBACK_URL=/mock-feedback.json
  // — which is the only way to see tallies without a deployed backend.
  //
  // Read path only. Casting a vote always goes to the API: the tally is computed
  // server-side from the voter's prior state, so there is deliberately no fixture
  // equivalent for writes (see contract-8).
  REACT_APP_FEEDBACK_URL: import.meta.env.REACT_APP_FEEDBACK_URL || "",
  REACT_APP_MARKETPLACE_NAME: import.meta.env.REACT_APP_MARKETPLACE_NAME || "skills-marketplace-catalog",
  REACT_APP_CATALOG_GIT_URL: import.meta.env.REACT_APP_CATALOG_GIT_URL || "https://ghe.coxautoinc.com/CoxAuto/skills-marketplace-catalog.git",
  // Identity of the contributed-skills write-path repo (issue #404). Used by
  // isContributedRecord (lib/contributed.ts) to gate Edit/Delete affordances to
  // records that actually live in this repo. Ships a hardcoded prod default (like
  // REACT_APP_CATALOG_GIT_URL) so an empty build var can't fail closed and make
  // Edit/Delete silently vanish for legitimate contributors.
  //
  // NOTE: this default is the PROD owner (CoxAuto). Non-prod's contributed-skills repo
  // is owned by Delivery-Platform, so a non-prod/ephemeral build MUST set this var
  // explicitly (the deploy scripts do, from the git_publisher_repo_owner tfvar). If a
  // non-prod build ever dropped to this default, it would parse cleanly to the WRONG
  // owner and Edit/Delete would fail closed for every non-prod record — WITHOUT tripping
  // warnMisconfiguredOnce, which only catches an empty/unparseable URL, not valid-but-wrong.
  REACT_APP_CONTRIBUTED_SKILLS_REPO_URL: import.meta.env.REACT_APP_CONTRIBUTED_SKILLS_REPO_URL || "https://ghe.coxautoinc.com/CoxAuto/contributed-skills.git",
  REACT_APP_GITHUB_PROXY_URL: import.meta.env.REACT_APP_GITHUB_PROXY_URL || "",
  REACT_APP_NEWRELIC_ACCOUNT_ID: import.meta.env.REACT_APP_NEWRELIC_ACCOUNT_ID || "",
  REACT_APP_NEWRELIC_APPLICATION_ID: import.meta.env.REACT_APP_NEWRELIC_APPLICATION_ID || "",
  REACT_APP_NEWRELIC_LICENSE_KEY: import.meta.env.REACT_APP_NEWRELIC_LICENSE_KEY || "",
  REACT_APP_NEWRELIC_TRUST_KEY: import.meta.env.REACT_APP_NEWRELIC_TRUST_KEY || "",
  REACT_APP_NEWRELIC_CI_ID: import.meta.env.REACT_APP_NEWRELIC_CI_ID || "",
  REACT_APP_GTM_CONTAINER_ID: import.meta.env.REACT_APP_GTM_CONTAINER_ID || "",
  /**
   * Gates the AI-mode UI: the "Ask AI" affordance, its answer panel, and the AI hint/status
   * line. Off unless explicitly "true" — so an environment whose `/api` backend isn't wired
   * never shows a non-functional "Ask AI" button. deploy-web-full.sh currently leaves it empty
   * for every env (the feature is paused); flip it back to "true" there once AI search is
   * deliberately turned back on (product-management#804). On also implies semantic search
   * (below), since Ask AI needs the same backend. This is purely a UI gate; the backend gate is
   * the `enable_ai_search` Terraform flag.
   */
  REACT_APP_AI_SEARCH_ENABLED: import.meta.env.REACT_APP_AI_SEARCH_ENABLED || "",
  /**
   * Gates hybrid search in the plain search box: the `/api/semantic` fetch whose hits are fused
   * with lexical results (#626). Independent of Ask AI, so semantic can ship while Ask AI stays
   * paused. Off unless explicitly "true". Off (or a backend that is down) degrades silently to
   * lexical-only search. Needs the `enable_ai_search` backend and a seeded embeddings.json.
   */
  REACT_APP_SEMANTIC_SEARCH_ENABLED: import.meta.env.REACT_APP_SEMANTIC_SEARCH_ENABLED || "",
  /**
   * Gates the MCP Servers section (#939): its side-nav item, the #/mcp pages and the MCP
   * Help topic. Off unless explicitly "true". deploy-web-full.sh and ephemeral-up.sh set it
   * in every environment; it's the section's off switch. The side nav itself isn't gated. With it off, #/mcp links land on the Skills catalog and Help
   * shows only Skills & Plugins.
   */
  REACT_APP_MCP_SERVERS_ENABLED: import.meta.env.REACT_APP_MCP_SERVERS_ENABLED || "",
  /**
   * Gates My Connections (#939, Feature H): its side-nav item, the #/connections page and the
   * recent-use box on each MCP server's page. Off unless explicitly "true". deploy-web-full.sh
   * turns it on for non-prod and leaves it off in prod; ephemeral-up.sh turns it on for previews.
   * Also needs REACT_APP_MCP_GATEWAY_URL: with no usable gateway there is nothing to frame, so
   * the feature stays off however this is set (see isMyConnectionsEnabled).
   */
  REACT_APP_MY_CONNECTIONS_ENABLED: import.meta.env.REACT_APP_MY_CONNECTIONS_ENABLED || "",
  /**
   * The MCP auth gateway the My Connections pages are framed from, e.g.
   * https://auth.mcp-np.ai.coxautoinc.com (non-prod and previews) or
   * https://auth.mcp-prod.ai.coxautoinc.com (prod). The gateway only lets the Marketplace of
   * the same environment frame its pages, so this must match the environment. Empty by
   * default, which turns My Connections off.
   */
  REACT_APP_MCP_GATEWAY_URL: import.meta.env.REACT_APP_MCP_GATEWAY_URL || "",
  /**
   * Gates the Skills & Plugins search pointer to MCP Servers (#939, Feature I): "Looking for a
   * connection to a system? N MCP servers match ...". Off unless explicitly "true", and it also
   * needs the MCP Servers section (REACT_APP_MCP_SERVERS_ENABLED), since that is where it points.
   * deploy-web-full.sh turns it on for non-prod and leaves it off in prod; ephemeral-up.sh turns it
   * on for previews.
   */
  REACT_APP_MCP_CROSS_SEARCH_ENABLED: import.meta.env.REACT_APP_MCP_CROSS_SEARCH_ENABLED || "",
  /**
   * Gates the Following section: its side-nav item and the #/following page. Off unless
   * explicitly "true". deploy-web-full.sh sets it for non-prod and ephemeral-up.sh for previews;
   * prod leaves it unset.
   */
  REACT_APP_FOLLOWING_ENABLED: import.meta.env.REACT_APP_FOLLOWING_ENABLED || "",
};
